import CapacityCounter from "@/src/components/events/event-form/capacity-counter";
import ScButton from "@/src/components/ui-kit/button";
import ScRadioOption from "@/src/components/ui-kit/radio-option";
import {
  eventRegistrationPolicy,
  Step2FormValues,
} from "@/src/models/event.model";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import ItemSelectorModal from "../../action-modals/item-selection/item-selection-modal";
import ScChips from "../../ui-kit/chips";
import { useAuth } from "@/src/contexts/auth.context";
import {
  useGroupPickerItems,
  useSelectedGroupItems,
} from "@/src/services/api/groups/groups.hooks";

type Step2SettingsProps = Readonly<{
  defaultValues: Partial<Step2FormValues> | null;
  onSubmit: (data: Step2FormValues) => void;
  onBack: () => void;
  onCancel: () => void;
}>;

const VISIBILITY_OPTIONS: {
  value: Step2FormValues["visibility"];
  label: string;
  description: string;
}[] = [
  {
    value: "Public",
    label: "LABEL_PUBLIC",
    description: "HINT_PUBLIC_VISIBILITY",
  },
  {
    value: "Group",
    label: "LABEL_GROUP",
    description: "HINT_GROUP_VISIBILITY",
  },
];

const POLICY_OPTIONS: {
  value: Step2FormValues["registrationPolicy"];
  label: string;
  description: string;
}[] = [
  {
    value: eventRegistrationPolicy.NONE,
    label: "LABEL_NONE",
    description: "HINT_NO_REGISTRATION",
  },
  {
    value: eventRegistrationPolicy.REQUIRED,
    label: "LABEL_REQUIRED",
    description: "HINT_REQUIRED_REGISTRATION",
  },
  // Invite-only is deliberately not offered. Choosing it produced an event whose
  // invite flow was never built — the participants view renders an Invite button
  // whose handler is a TODO — so the event could not be joined by anyone. The
  // enum value stays so existing events created under it still read correctly.
];
const Step2Settings = ({
  defaultValues,
  onSubmit,
  onBack,
  onCancel,
}: Step2SettingsProps) => {
  const { t } = useTranslation();

  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const {
    control,
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<Step2FormValues>({
    defaultValues: {
      visibility: "Public",
      registrationPolicy: eventRegistrationPolicy.NONE,
      capacity: null,
      groupIds: [],
      ...defaultValues,
    },
  });

  const visibility = watch("visibility");
  const groupIds = watch("groupIds");
  // Chips resolve by id, so a group past the picker's loaded pages (or picked
  // in an earlier session, when editing) still shows.
  const selectedGroups = useSelectedGroupItems(groupIds);

  // Anyone can create an event, so — like posts — it can only target groups
  // the creator has joined. `userGroups` is null until memberships load, so
  // the list stays empty rather than briefly offering every group.
  const { userGroups } = useAuth();
  const joinedGroupIds = userGroups ?? [];
  const groupPicker = useGroupPickerItems(
    { ItemId: { in: joinedGroupIds } },
    groupModalVisible && joinedGroupIds.length > 0,
  );
  const registrationPolicy = watch("registrationPolicy");

  useEffect(() => {
    if (registrationPolicy !== eventRegistrationPolicy.REQUIRED) {
      setValue("capacity", null);
    }
  }, [registrationPolicy, setValue]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    if (visibility !== "Group") {
      setValue("groupIds", []);
    }
  }, [visibility, setValue]);

  const handleGroupConfirm = (ids: string[]) => {
    setValue("groupIds", ids);
  };

  const handleRemoveGroup = (id: string) => {
    setValue(
      "groupIds",
      (groupIds ?? []).filter((groupId) => groupId !== id),
    );
  };

  return (
    <View>
      <View className="mb-4">
        <Controller
          control={control}
          name="groupIds"
          rules={{
            validate: (val) =>
              visibility !== "Group" ||
              (val && val.length > 0) ||
              t("ERROR_SELECT_AT_LEAST_ONE_GROUP"),
          }}
          render={() => <></>}
        />

        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_VISIBILITY_OPTIONS")}
        </Text>
        <Controller
          control={control}
          name="visibility"
          render={({ field }) => (
            <View className="rounded-xl overflow-hidden md:border md:border-ash-3">
              {VISIBILITY_OPTIONS.map((option, index) => (
                <React.Fragment key={option.value}>
                  {index > 0 && <View className="h-px bg-ash-3" />}
                  <ScRadioOption
                    selected={field.value === option.value}
                    onSelect={() => {
                      field.onChange(option.value);
                      if (option.value === "Group") {
                        setGroupModalVisible(true);
                      }
                    }}
                    label={option.label}
                    description={option.description}
                  />
                  {option.value === "Group" && field.value === "Group" && (
                    <View className="px-4 pb-3 pl-12 gap-2 mt-2">
                      {selectedGroups.length > 0 ? (
                        <ScChips
                          items={selectedGroups}
                          onRemove={handleRemoveGroup}
                        />
                      ) : (
                        <ScButton
                          text={t("BTN_SELECT_GROUPS")}
                          variant="pill"
                          className="self-start"
                          onPress={() => setGroupModalVisible(true)}
                        />
                      )}

                      {selectedGroups.length > 0 && (
                        <>
                          <ScButton
                            text={t("BTN_EDIT_SELECTED_GROUPS")}
                            variant="pill"
                            className="self-start"
                            onPress={() => setGroupModalVisible(true)}
                          />
                        </>
                      )}
                      {errors.groupIds && (
                        <Text className="text-red-500 text-xs">
                          {errors.groupIds.message}
                        </Text>
                      )}
                    </View>
                  )}
                </React.Fragment>
              ))}
            </View>
          )}
        />
      </View>

      <View className="mb-4">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_REGISTRATION_POLICY")}
        </Text>
        <Controller
          control={control}
          name="registrationPolicy"
          render={({ field }) => (
            <View className="rounded-xl overflow-hidden md:border md:border-ash-3">
              {POLICY_OPTIONS.map((option, index) => (
                <React.Fragment key={option.value}>
                  {index > 0 && <View className="h-px bg-ash-3" />}
                  <ScRadioOption
                    selected={field.value === option.value}
                    onSelect={() => field.onChange(option.value)}
                    label={option.label}
                    description={option.description}
                  />
                  {option.value === "Required" &&
                    field.value === "Required" && (
                      <View className="px-4 pb-3 pl-12">
                        <Text className="text-xs text-gray-dark mb-2">
                          {t("LABEL_EVENT_CAPACITY")}
                        </Text>
                        <Controller
                          control={control}
                          name="capacity"
                          render={({ field: capacityField }) => (
                            <CapacityCounter
                              value={capacityField.value ?? 1}
                              onChange={capacityField.onChange}
                            />
                          )}
                        />
                      </View>
                    )}
                </React.Fragment>
              ))}
            </View>
          )}
        />
      </View>

      <View className="flex-row justify-between">
        <ScButton text={t("BTN_CANCEL")} mode="outlined" onPress={onCancel} />
        <View className="flex-row gap-4">
          <ScButton text={t("BTN_PREVIOUS")} mode="outlined" onPress={onBack} />
          <ScButton text={t("BTN_CONTINUE")} onPress={handleSubmit(onSubmit)} />
        </View>
      </View>
      <ItemSelectorModal
        items={groupPicker.items}
        selectedItems={selectedGroups}
        visible={groupModalVisible}
        onClose={() => setGroupModalVisible(false)}
        onConfirm={handleGroupConfirm}
        titleKey="LABEL_SELECT_GROUPS"
        confirmButtonKey="BTN_ADD"
        searchPlaceholderKey="LABEL_SEARCH"
        emptyLabelKey="LABEL_NO_JOINED_GROUPS"
        onSearchChange={groupPicker.onSearchChange}
        onEndReached={groupPicker.onEndReached}
        isFetchingMore={groupPicker.isFetchingMore}
        totalCount={groupPicker.totalCount}
      />
    </View>
  );
};

export default Step2Settings;
